import Button from "@mui/material/Button/Button";
import Card from "@mui/material/Card/Card";
import Divider from "@mui/material/Divider/Divider";
import List from "@mui/material/List/List";
import ListItem from "@mui/material/ListItem/ListItem";
import ListItemText from "@mui/material/ListItemText/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography/Typography";
import { MdsKeyboardArrowDown, MdsKeyboardArrowUp } from "@sinch/icons";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components/index";

interface CollapsibleListCardProps {
  /** The summary text to display in the link (e.g., "3 ratelists", "N usages"). */
  summaryText: string;
  /** An array of strings representing the items to be displayed in the list. Each string will be the primary text of a list item. */
  items: ReactNode[];
}

/**
 * CollapsibleListCard component
 *
 * Displays a card with a summary line (e.g., "Used in 3 ratelists") containing a link.
 * Clicking the link or the show/hide button reveals or conceals a list of items.
 * The component manages its own expanded/collapsed state.
 */
export const CollapsibleListCard: React.FC<CollapsibleListCardProps> = ({ summaryText, items }) => {
  const { t } = useTranslation();
  const [isListOpen, setIsListOpen] = useState(false);

  // Toggles the visibility of the list
  const toggleList = () => setIsListOpen((prev) => !prev);

  return (
    <Card variant="outlined">
      <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ pr: 0.5 }}>
        <Typography sx={{ p: 1 }} variant="subtitle2">
          {summaryText}
        </Typography>
        <Button
          onClick={toggleList}
          size="small"
          startIcon={<MdsIcon icon={isListOpen ? MdsKeyboardArrowUp : MdsKeyboardArrowDown} />}
          variant="text"
        >
          {isListOpen ? t("close") : t("show")}
        </Button>
      </Stack>
      {isListOpen && (
        <>
          <Divider />
          <List
            sx={{
              display: "initial",
              maxWidth: 360,
              bgcolor: "background.paper",
              position: "relative",
              overflow: "auto",
              maxHeight: 300,
              "& ul": { padding: 0 },
            }}
          >
            {items.map((item, index) => (
              <ListItem key={`collapsible-list-item-${index}`} sx={{ py: 0 }}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Card>
  );
};
